import type { MaybeRefOrGetter } from 'vue';

import { computed, onMounted, onScopeDispose, readonly, shallowRef, toValue } from 'vue';

import {
  browserScrollBoundaryAdapter,
  createIdlePullGestureState,
  isPullToRefreshBoundary,
  isPullToRefreshIgnoredTarget,
  reducePullGesture,
  type PullGesturePhase,
  type PullGestureState,
} from '../lib';

interface TouchPointLike {
  clientX: number;
  clientY: number;
}

interface TouchEventLike extends Event {
  touches: ArrayLike<TouchPointLike>;
}

export interface PullToRefreshEventTarget {
  addEventListener: (type: string, listener: EventListener, options?: AddEventListenerOptions | boolean) => void;
  removeEventListener: (type: string, listener: EventListener, options?: EventListenerOptions | boolean) => void;
}

export interface PullToRefreshListenerOptions {
  getTarget: () => PullToRefreshEventTarget | null;
  isEnabled: () => boolean;
  isAtBoundary: (target: EventTarget) => boolean;
  isIgnoredTarget: (target: EventTarget) => boolean;
  onRefresh: () => Promise<unknown>;
  onStateChange?: (state: PullGestureState) => void;
  warn?: (context: { kind: 'listener-init-failed' | 'listener-cleanup-failed' | 'refresh-rejected' }) => void;
}

export interface PullToRefreshListenerController {
  bind: () => void;
  dispose: () => void;
}

const LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };
const MOVE_LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: false };

export const createPullToRefreshListenerController = (
  options: PullToRefreshListenerOptions,
): PullToRefreshListenerController => {
  let state = createIdlePullGestureState();
  let activeTarget: EventTarget | null = null;
  let boundTarget: PullToRefreshEventTarget | null = null;

  const updateState = (nextState: PullGestureState): void => {
    state = nextState;
    options.onStateChange?.(state);
  };
  const reset = (): void => {
    activeTarget = null;
    updateState(createIdlePullGestureState());
  };
  const getTouch = (event: TouchEventLike): TouchPointLike | null => {
    return event.touches.length === 1 ? (event.touches[0] ?? null) : null;
  };
  const handleStart = (rawEvent: Event): void => {
    const event = rawEvent as TouchEventLike;
    const touch = getTouch(event);
    const target = event.target;
    const canStart =
      options.isEnabled() &&
      touch !== null &&
      target !== null &&
      !options.isIgnoredTarget(target) &&
      options.isAtBoundary(target);
    const transition = reducePullGesture(state, {
      type: 'start',
      x: touch?.clientX ?? 0,
      y: touch?.clientY ?? 0,
      touchCount: event.touches.length,
      canStart,
    });

    activeTarget = canStart ? target : null;
    updateState(transition.state);
  };
  const handleMove = (rawEvent: Event): void => {
    if (!options.isEnabled()) {
      reset();
      return;
    }

    const event = rawEvent as TouchEventLike;
    const touch = getTouch(event);
    const transition = reducePullGesture(
      state,
      touch
        ? {
            type: 'move',
            x: touch.clientX,
            y: touch.clientY,
            touchCount: event.touches.length,
            isAtBoundary: activeTarget !== null && options.isAtBoundary(activeTarget),
          }
        : { type: 'cancel' },
    );

    updateState(transition.state);
    if (transition.shouldPreventDefault && event.cancelable) {
      event.preventDefault();
    }
  };
  const handleEnd = (): void => {
    const transition = reducePullGesture(state, { type: 'release' });
    reset();

    if (transition.shouldRefresh && options.isEnabled()) {
      void Promise.resolve()
        .then(options.onRefresh)
        .catch(() => {
          options.warn?.({ kind: 'refresh-rejected' });
        });
    }
  };
  const handleCancel = (): void => {
    reset();
  };

  const listeners = [
    ['touchstart', handleStart, LISTENER_OPTIONS],
    ['touchmove', handleMove, MOVE_LISTENER_OPTIONS],
    ['touchend', handleEnd as EventListener, LISTENER_OPTIONS],
    ['touchcancel', handleCancel as EventListener, LISTENER_OPTIONS],
  ] as const;

  const dispose = (): void => {
    if (!boundTarget) {
      reset();
      return;
    }

    try {
      for (const [type, listener, listenerOptions] of listeners) {
        boundTarget.removeEventListener(type, listener, listenerOptions);
      }
    } catch {
      options.warn?.({ kind: 'listener-cleanup-failed' });
    } finally {
      boundTarget = null;
      reset();
    }
  };

  return {
    bind: () => {
      const target = options.getTarget();
      if (!target || boundTarget === target) {
        return;
      }

      dispose();
      try {
        for (const [type, listener, listenerOptions] of listeners) {
          target.addEventListener(type, listener, listenerOptions);
        }
        boundTarget = target;
      } catch {
        boundTarget = target;
        dispose();
        options.warn?.({ kind: 'listener-init-failed' });
      }
    },
    dispose,
  };
};

export interface UsePullToRefreshOptions {
  target: MaybeRefOrGetter<EventTarget | null>;
  enabled: MaybeRefOrGetter<boolean>;
  onRefresh: () => Promise<unknown>;
}

export interface PullIndicatorPresentation {
  isVisible: boolean;
  label: string;
  shouldAnimatePosition: boolean;
}

export const resolvePullIndicatorPresentation = ({
  phase,
  isRefreshing,
  prefersReducedMotion,
}: {
  phase: PullGesturePhase;
  isRefreshing: boolean;
  prefersReducedMotion: boolean;
}): PullIndicatorPresentation => ({
  isVisible: isRefreshing || phase === 'pulling' || phase === 'armed',
  label: isRefreshing
    ? 'Обновление данных'
    : phase === 'armed'
      ? 'Отпустите для обновления'
      : 'Потяните для обновления',
  shouldAnimatePosition: !prefersReducedMotion && !isRefreshing,
});

export const usePullToRefresh = (options: UsePullToRefreshOptions) => {
  const gestureState = shallowRef(createIdlePullGestureState());
  const controller = createPullToRefreshListenerController({
    getTarget: () => toValue(options.target),
    isEnabled: () => toValue(options.enabled),
    isAtBoundary: (target) =>
      target instanceof Node &&
      isPullToRefreshBoundary({
        target,
        adapter: browserScrollBoundaryAdapter,
      }),
    isIgnoredTarget: (target) =>
      target instanceof Node && isPullToRefreshIgnoredTarget(target, browserScrollBoundaryAdapter),
    onRefresh: options.onRefresh,
    onStateChange: (state) => {
      gestureState.value = state;
    },
    warn: (context) => {
      console.warn('[data-refresh] Pull-to-refresh integration failed', context);
    },
  });

  onMounted(controller.bind);
  onScopeDispose(controller.dispose);

  return {
    phase: computed(() => gestureState.value.phase),
    pullDistance: computed(() => gestureState.value.pullDistance),
    isArmed: computed(() => gestureState.value.phase === 'armed'),
    gestureState: readonly(gestureState),
  };
};
